import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { canAccess } from '../utils/tasks';

// Shown when the admin switched off the task a page needs. Not a redirect:
// landing on the login screen signs you out, so bouncing there would log the
// employee out just for opening a page they can't use.
const TaskSwitchedOff = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ maxWidth: 420, textAlign: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: '1.5rem' }}>
        <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem', color: '#0f172a' }}>{t('tasks.switchedOffTitle')}</h2>
        <p style={{ margin: '0 0 1rem', color: '#64748b', fontSize: '0.9rem', lineHeight: 1.5 }}>{t('tasks.switchedOffBody')}</p>
        <button type="button" onClick={() => navigate(-1)}
          style={{ border: '1px solid #cbd5e1', background: '#fff', borderRadius: 10, padding: '0.55rem 1.1rem', fontWeight: 600, cursor: 'pointer' }}>
          {t('tasks.goBack')}
        </button>
      </div>
    </div>
  );
};

// `requiredTask` (one task or a list) works with `requiredRole`: the page opens
// for anyone holding one of the tasks, and for a listed role unless the admin
// switched that role's task off. See utils/tasks.js canAccess().
const ProtectedRoute = ({ children, requiredRole = null, requiredTask = null }) => {
  const token = localStorage.getItem('token');
  const userStr = localStorage.getItem('user');

  // Check if user is logged in
  if (!token || !userStr) {
    return <Navigate to="/" replace />;
  }

  try {
    const user = JSON.parse(userStr);

    // Check if user is active (if field exists, it must be true)
    if (user.isActive === false) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return <Navigate to="/" replace />;
    }

    // Advisors open every dashboard an admin can (super-admin pages excepted).
    const actsAsAdmin = user.role === 'admin' || user.role === 'advisor';

    if (requiredTask && user.role !== 'super_admin' && !actsAsAdmin) {
      const roles = requiredRole ? (Array.isArray(requiredRole) ? requiredRole : [requiredRole]) : null;
      if (canAccess(user, requiredTask, roles)) return children;
      if (roles && !roles.includes(user.role)) {
        console.error('ProtectedRoute: Role mismatch:', { required: requiredRole, userRole: user.role });
        return <Navigate to="/" replace />;
      }
      return <TaskSwitchedOff />;
    }

    // Check role if required. super_admin bypasses everything. admin bypasses
    // everything too — so it can open every dashboard — EXCEPT super-admin-only
    // routes, which stay reserved for super_admin.
    if (requiredRole && user.role !== 'super_admin') {
      const allowedRoles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
      const adminCanOpen = actsAsAdmin && !allowedRoles.includes('super_admin');
      if (!adminCanOpen && !allowedRoles.includes(user.role)) {
        console.error('ProtectedRoute: Role mismatch:', { required: requiredRole, userRole: user.role });
        return <Navigate to="/" replace />;
      }
    }

    return children;
  } catch (error) {
    console.error('ProtectedRoute: Error parsing user data:', error);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    return <Navigate to="/" replace />;
  }
};

export default ProtectedRoute;
